import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, RefreshCw, LogOut, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminLeads() {
  const [token, setToken] = useState(
    () => localStorage.getItem("ga_admin_token") || ""
  );
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | error | ready
  const [error, setError] = useState("");

  const fetchLeads = async (t) => {
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/leads`, {
        headers: { "x-admin-token": t },
      });
      if (res.status === 401) {
        setError("Incorrect access code. Please try again.");
        setStatus("error");
        localStorage.removeItem("ga_admin_token");
        setToken("");
        return;
      }
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setLeads(data);
      setStatus("ready");
      localStorage.setItem("ga_admin_token", t);
    } catch (e) {
      setError("Could not load leads. Please try again.");
      setStatus("error");
    }
  };

  useEffect(() => {
    if (token) fetchLeads(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setToken(input.trim());
    fetchLeads(input.trim());
  };

  const logout = () => {
    localStorage.removeItem("ga_admin_token");
    setToken("");
    setLeads([]);
    setStatus("idle");
    setInput("");
  };

  const loggedIn = token && (status === "ready" || status === "loading");

  return (
    <div className="min-h-screen bg-[#f4f1e9]">
      <header className="border-b border-[#e2ddcf] bg-[#f4f1e9]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <Shield className="h-7 w-7 text-[#223f2e]" strokeWidth={2} />
            <span className="font-display text-[22px] font-semibold text-[#223f2e]">
              Guardian Advisory
            </span>
          </Link>
          <span className="font-mono-label text-[11px] text-[#4f7a5e]">
            Lead Inbox
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        {!loggedIn ? (
          <div className="mx-auto mt-10 max-w-md rounded-[26px] border border-[#ece7d9] bg-white p-8 shadow-[0_40px_80px_-45px_rgba(34,63,46,0.35)]">
            <h1 className="font-display text-[28px] font-semibold text-[#1e3a2a]">
              Lead Inbox
            </h1>
            <p className="mt-2 text-[15px] text-[#566257]">
              Enter your access code to view submitted leads.
            </p>
            <form onSubmit={handleLogin} className="mt-6">
              <input
                data-testid="admin-token-input"
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Access code"
                className="w-full rounded-xl border border-[#e0dccd] bg-[#faf8f2] px-4 py-3 text-[15px] text-[#1e3a2a] outline-none transition-colors focus:border-[#bf9a4d] focus:bg-white"
              />
              {error && (
                <p data-testid="admin-error" className="mt-3 text-[13px] font-medium text-[#a55a38]">
                  {error}
                </p>
              )}
              <button
                data-testid="admin-login-btn"
                type="submit"
                className="mt-4 w-full rounded-full bg-[#bf9a4d] px-6 py-3.5 text-[15px] font-semibold text-[#1c3325] transition-colors hover:bg-[#a9853c]"
              >
                View Leads
              </button>
            </form>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-display text-[32px] font-semibold text-[#1e3a2a]">
                  Leads
                </h1>
                <p className="mt-1 text-[15px] text-[#566257]">
                  {leads.length} total submission{leads.length === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="admin-refresh-btn"
                  onClick={() => fetchLeads(token)}
                  className="inline-flex items-center gap-2 rounded-full border border-[#d9d3c4] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#223f2e] transition-colors hover:bg-[#faf8f2]"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${status === "loading" ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>
                <button
                  data-testid="admin-logout-btn"
                  onClick={logout}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[14px] font-semibold text-[#566257] transition-colors hover:text-[#223f2e]"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            </div>

            {leads.length === 0 && status === "ready" ? (
              <div className="mt-10 flex flex-col items-center rounded-[26px] border border-dashed border-[#d9d3c4] bg-white/60 p-16 text-center">
                <Inbox className="h-10 w-10 text-[#9db4a2]" />
                <p className="mt-4 text-[16px] font-medium text-[#1e3a2a]">
                  No leads yet
                </p>
                <p className="mt-1 text-[14px] text-[#566257]">
                  New form submissions will appear here automatically.
                </p>
              </div>
            ) : (
              <div className="mt-8 overflow-x-auto rounded-[20px] border border-[#ece7d9] bg-white shadow-[0_30px_60px_-50px_rgba(34,63,46,0.4)]">
                <table className="w-full min-w-[820px] border-collapse text-left" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-[#ece7d9] bg-[#faf8f2]">
                      {["Received", "Name", "Phone", "Email", "Business", "MCA Debt", "Positions"].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-5 py-4 font-mono-label text-[10.5px] text-[#6b7268]"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr
                        key={lead.id}
                        data-testid="lead-row"
                        className="border-b border-[#f0ecdf] last:border-0 hover:bg-[#faf8f2]"
                      >
                        <td className="whitespace-nowrap px-5 py-4 text-[13px] text-[#8a8f85]">
                          {fmtDate(lead.created_at)}
                        </td>
                        <td className="px-5 py-4 text-[14px] font-semibold text-[#1e3a2a]">
                          {lead.full_name}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-[14px] text-[#4b5a4f]">
                          <a href={`tel:${lead.phone}`} className="hover:underline">
                            {lead.phone}
                          </a>
                        </td>
                        <td className="px-5 py-4 text-[14px] text-[#4b5a4f]">
                          <a href={`mailto:${lead.email}`} className="hover:underline">
                            {lead.email}
                          </a>
                        </td>
                        <td className="px-5 py-4 text-[14px] text-[#4b5a4f]">
                          {lead.business_name}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-[14px] text-[#4b5a4f]">
                          {lead.total_debt}
                        </td>
                        <td className="px-5 py-4 text-[14px] text-[#4b5a4f]">
                          {lead.active_positions}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
